import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientAPI } from '../../../services';
import { useAsyncAction } from '../../../hooks';
import {
  Button,
  Modal,
  Input,
  Badge,
  LoadingSpinner,
} from '../../UI';
import { Building2, Plus, Eye, Edit, Trash2, Play, Pause, Key, Users, DollarSign, Package, Receipt, CheckCircle, Filter, RefreshCw } from 'lucide-react';
import {
  C, FONT_UI, Mono, Card, Pill, PageHeader, PrimaryButton, GhostButton, IconButton,
  Toolbar, ChipTabs, OrgAvatar, EmptyState, Banner, fmtDate, daysUntil,
} from './theme';
import { useAuth } from '../../../context/AuthContext';

const ClientsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = (action) => user?.role !== 'platform_staff' || user?.platformPermissions?.clients?.[action] === true;
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const { loading: deleteLoading, execute: executeDelete } = useAsyncAction();
  const { execute: executeToggle } = useAsyncAction();
  const { loading: resetLoading, execute: executeReset } = useAsyncAction();
  const { loading: billingLoading, execute: executeBilling } = useAsyncAction();
  
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [billingData, setBillingData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [deletePreview, setDeletePreview] = useState(null);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);

  // List controls
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const clientsRes = await clientAPI.getClients({ page: 1, limit: 100 });
      if (clientsRes.status === 'success') setClients(clientsRes.data.clients || []);
    } catch (err) {
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = async (client) => {
    setSelected(client);
    setDeletePreview(null);
    setDeletePreviewLoading(true);
    setIsDeleteModalOpen(true);
    
    try {
      const response = await clientAPI.getDeletePreview(client._id);
      if (response.status === 'success') {
        setDeletePreview(response.data);
      }
    } catch (err) {
      console.error('Failed to get delete preview:', err);
    } finally {
      setDeletePreviewLoading(false);
    }
  };

  const handleDelete = async () => {
    const result = await executeDelete(clientAPI.deleteClient, selected._id);
    if (result.success) {
      setSuccess('Client and all related data deleted successfully');
      setIsDeleteModalOpen(false);
      setDeletePreview(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Delete failed');
    }
  };

  const handleToggleStatus = async (client) => {
    const result = await executeToggle(clientAPI.toggleClientStatus, client._id);
    if (result.success) {
      setSuccess(`Client ${client.active ? 'suspended' : 'activated'}`);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Toggle failed');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const result = await executeReset(clientAPI.resetAdminPassword, selected._id, { newPassword });
    if (result.success) {
      setSuccess('Password reset successfully');
      setIsResetPasswordModalOpen(false);
      setNewPassword('');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Reset failed');
    }
  };

  const openBillingModal = async (client) => {
    setSelected(client);
    setIsBillingModalOpen(true);
    try {
      const result = await clientAPI.getClientBilling(client._id);
      if (result.status === 'success') {
        setBillingData(result.data);
      }
    } catch (err) {
      setError('Failed to load billing data');
    }
  };

  const handleGenerateBilling = async () => {
    if (!selected) return;
    const result = await executeBilling(clientAPI.generateBilling, selected._id);
    if (result.success) {
      setSuccess('Billing generated successfully');
      // Refresh billing data
      const refreshResult = await clientAPI.getClientBilling(selected._id);
      if (refreshResult.status === 'success') {
        setBillingData(refreshResult.data);
      }
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to generate billing');
    }
  };

  const handleMarkPaid = async (month) => {
    if (!selected) return;
    const result = await executeBilling(clientAPI.markPaymentPaid, selected._id, { month, paymentMethod: 'manual' });
    if (result.success) {
      setSuccess('Payment marked as paid');
      // Refresh billing data
      const refreshResult = await clientAPI.getClientBilling(selected._id);
      if (refreshResult.status === 'success') {
        setBillingData(refreshResult.data);
      }
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to mark payment');
    }
  };

  const filtered = clients.filter((c) => {
    const q = search.trim().toLowerCase();
    if (q && !`${c.name} ${c.email || ''} ${c.code || ''}`.toLowerCase().includes(q)) return false;
    if (statusFilter === 'active' && !c.active) return false;
    if (statusFilter === 'suspended' && c.active) return false;
    if (typeFilter && c.subscription?.type !== typeFilter) return false;
    if (planFilter && c.subscription?.plan !== planFilter) return false;
    return true;
  });

  // Trials expire on trialEndDate; paid subscriptions on endDate.
  const expiryOf = (c) =>
    c.subscription?.type === 'trial' ? c.subscription?.trialEndDate : c.subscription?.endDate;

  const counts = {
    all: clients.length,
    active: clients.filter((c) => c.active).length,
    suspended: clients.filter((c) => !c.active).length,
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: FONT_UI }}>
      <Banner tone="error" message={error} onClose={() => setError('')} />
      <Banner tone="success" message={success} onClose={() => setSuccess('')} />

      <PageHeader
        title="Clients"
        subtitle={`${clients.length} organization${clients.length === 1 ? '' : 's'} on the platform`}
        actions={
          canManage('create') && (
            <PrimaryButton icon={Plus} onClick={() => navigate('/dashboard/clients/add')}>
              Add new client
            </PrimaryButton>
          )
        }
      />

      <Toolbar value={search} onChange={setSearch} placeholder="Search by client name...">
        <GhostButton icon={Filter} onClick={() => setShowFilters((v) => !v)}>
          Filters
        </GhostButton>
      </Toolbar>

      {showFilters && (
        <Card className="p-4 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: C.textFaint }}>
                Status
              </p>
              <ChipTabs
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: '', label: `All (${counts.all})` },
                  { value: 'active', label: `Active (${counts.active})` },
                  { value: 'suspended', label: `Suspended (${counts.suspended})` },
                ]}
              />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: C.textFaint }}>
                Subscription
              </p>
              <ChipTabs
                value={typeFilter}
                onChange={setTypeFilter}
                options={[
                  { value: '', label: 'Any' },
                  { value: 'trial', label: 'Trial' },
                  { value: 'paid', label: 'Paid' },
                ]}
              />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: C.textFaint }}>
                Plan
              </p>
              <ChipTabs
                value={planFilter}
                onChange={setPlanFilter}
                options={[
                  { value: '', label: 'Any' },
                  { value: 'basic', label: 'Basic' },
                  { value: 'premium', label: 'Premium' },
                  { value: 'enterprise', label: 'Enterprise' },
                ]}
              />
            </div>
          </div>
        </Card>
      )}

      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((client) => {
            const expiry = expiryOf(client);
            const left = daysUntil(expiry);
            const expired = left !== null && left < 0;
            const urgent = left !== null && left >= 0 && left <= 14;
            const stripBg = expired ? '#FEF2F2' : urgent ? '#FFFBEB' : '#F8FAFC';
            const stripFg = expired ? '#B91C1C' : urgent ? '#B45309' : C.textMuted;

            return (
              <Card key={client._id} className="overflow-hidden transition-shadow hover:shadow-md">
                <div className="p-5 pb-4">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <OrgAvatar name={client.name} logoUrl={client.logo?.url} size={40} />
                    <div className="flex items-center gap-1.5">
                      {!client.active && <Pill tone="danger">Suspended</Pill>}
                      <Pill tone={client.subscription?.type === 'trial' ? 'success' : 'info'}>
                        {client.subscription?.type === 'trial' ? 'Trial' : 'Paid'}
                      </Pill>
                    </div>
                  </div>

                  <h3 className="text-[15px] font-semibold truncate" style={{ color: C.ink }}>
                    {client.name}
                  </h3>
                  <Mono className="block text-[11px] truncate mt-0.5" style={{ color: C.textMuted }}>
                    {client.email || client.code || 'No email'}
                  </Mono>

                  <div className="flex items-center justify-between mt-4">
                    <Mono className="text-[12px]" style={{ color: C.ink }}>
                      {client.userCount || 0}/{client.subscription?.employeeLimit || 0} employees
                    </Mono>
                    <span className="text-[12px] capitalize" style={{ color: C.textMuted }}>
                      {client.subscription?.plan || 'basic'} plan
                    </span>
                  </div>
                </div>

                {/* Expiry strip */}
                <div
                  className="flex items-center justify-between px-4 py-2.5 mx-4 rounded-lg"
                  style={{ background: stripBg }}
                >
                  <Mono className="text-[11px]" style={{ color: stripFg }}>
                    {expired ? 'Expired' : 'Expires'} {fmtDate(expiry)}
                    {urgent && !expired ? ` · ${left}d left` : ''}
                  </Mono>
                  {canManage('update') && (
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/clients/${client._id}/edit`)}
                      className="inline-flex items-center gap-1 text-[11px] font-medium"
                      style={{ color: stripFg }}
                      title="Extend this client's subscription"
                    >
                      <RefreshCw className="w-3 h-3" /> Renew
                    </button>
                  )}
                </div>

                {/* Actions */}
                <div
                  className="flex items-center justify-around mt-4 px-2 py-1.5"
                  style={{ borderTop: `1px solid ${C.border}` }}
                >
                  <IconButton
                    icon={Eye}
                    title="View details"
                    onClick={() => { setSelected(client); setIsViewModalOpen(true); }}
                  />
                  {canManage('update') && (
                    <IconButton
                      icon={Edit}
                      title="Edit client"
                      onClick={() => navigate(`/dashboard/clients/${client._id}/edit`)}
                    />
                  )}
                  {canManage('read') && (
                    <IconButton icon={DollarSign} title="Billing" onClick={() => openBillingModal(client)} />
                  )}
                  {canManage('update') && (
                    <IconButton
                      icon={client.active ? Pause : Play}
                      title={client.active ? 'Suspend client' : 'Activate client'}
                      tone={client.active ? C.amber : C.emerald}
                      onClick={() => handleToggleStatus(client)}
                    />
                  )}
                  {canManage('update') && (
                    <IconButton
                      icon={Key}
                      title="Reset admin password"
                      onClick={() => { setSelected(client); setIsResetPasswordModalOpen(true); }}
                    />
                  )}
                  {canManage('delete') && (
                    <IconButton icon={Trash2} title="Delete client" tone={C.rose} onClick={() => openDeleteModal(client)} />
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <EmptyState
            icon={Building2}
            title={clients.length ? 'No clients match these filters' : 'No clients yet'}
            message={
              clients.length
                ? 'Try clearing the search box or widening the filters.'
                : 'Add your first client to start tracking subscriptions and billing.'
            }
            action={
              clients.length
                ? <GhostButton onClick={() => { setSearch(''); setStatusFilter(''); setTypeFilter(''); setPlanFilter(''); }}>Clear filters</GhostButton>
                : canManage('create') && (
                    <PrimaryButton icon={Plus} onClick={() => navigate('/dashboard/clients/add')}>
                      Add new client
                    </PrimaryButton>
                  )
            }
          />
        </Card>
      )}

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Client Details" size="lg">
        {selected && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center gap-4 pb-4 border-b">
              {selected.logo?.url ? (
                <img src={selected.logo.url} alt={selected.name} className="w-16 h-16 rounded-xl object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-2xl">
                  {selected.name?.charAt(0)?.toUpperCase()}
                </div>
              )}
              <div>
                <h3 className="text-xl font-bold">{selected.name}</h3>
                <div className="flex items-center gap-2">
                  <Badge variant={selected.active ? 'success' : 'danger'}>
                    {selected.active ? 'Active' : 'Suspended'}
                  </Badge>
                  <Badge variant={selected.subscription?.type === 'trial' ? 'warning' : 'primary'}>
                    {selected.subscription?.type === 'trial' ? 'Trial' : 'Paid'} - {selected.subscription?.plan || 'basic'}
                  </Badge>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-sm text-gray-500">Email</p><p className="font-medium">{selected.email || 'N/A'}</p></div>
              <div><p className="text-sm text-gray-500">Phone</p><p className="font-medium">{selected.phone || 'N/A'}</p></div>
              <div><p className="text-sm text-gray-500">Employees</p><p className="font-medium">{selected.userCount || 0} / {selected.subscription?.employeeLimit || 10}</p></div>
              <div><p className="text-sm text-gray-500">Locations</p><p className="font-medium">{selected.locationCount || 0}</p></div>
              <div><p className="text-sm text-gray-500">Created</p><p className="font-medium">{new Date(selected.createdAt).toLocaleDateString()}</p></div>
              {selected.subscription?.type === 'trial' ? (
                <div>
                  <p className="text-sm text-gray-500">Trial Period</p>
                  <p className="font-medium">
                    {selected.subscription?.trialStartDate ? new Date(selected.subscription.trialStartDate).toLocaleDateString() : 'N/A'} 
                    {' - '}
                    {selected.subscription?.trialEndDate ? new Date(selected.subscription.trialEndDate).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-500">Subscription Ends</p>
                  <p className="font-medium">{selected.subscription?.endDate ? new Date(selected.subscription.endDate).toLocaleDateString() : 'N/A'}</p>
                </div>
              )}
            </div>
            
            {/* Pricing Info */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Pricing
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-500">Per Employee Cost</p>
                  <p className="font-bold text-lg">₹{selected.subscription?.perEmployeeCost || 0}/mo</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-500">Actual Cost</p>
                  <p className="font-bold text-lg">₹{(selected.userCount || 0) * (selected.subscription?.perEmployeeCost || 0)}/mo</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-500">Payment Due</p>
                  <p className="font-bold text-lg text-green-600">
                    {selected.subscription?.type === 'trial' ? '₹0 (Trial)' : `₹${(selected.userCount || 0) * (selected.subscription?.perEmployeeCost || 0)}`}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Module Access */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Package className="w-4 h-4" /> Module Access
              </h4>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'employees', label: 'Employees' },
                  { key: 'customers', label: 'Customers' },
                  { key: 'locations', label: 'Locations' },
                  { key: 'attendance', label: 'Attendance' },
                  { key: 'leaves', label: 'Leaves' },
                  { key: 'payroll', label: 'Payroll' },
                  { key: 'holidays', label: 'Holidays' }
                ].map(module => (
                  <Badge 
                    key={module.key} 
                    variant={selected.subscription?.modules?.[module.key] ? 'success' : 'secondary'}
                  >
                    {module.label}: {selected.subscription?.modules?.[module.key] ? '✓' : '✗'}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => { setIsDeleteModalOpen(false); setDeletePreview(null); }} title="Delete Client" size="md">
        <div className="text-center">
          <Trash2 className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">Are you sure you want to delete</p>
          <p className="font-semibold text-gray-900 mb-4">{selected?.name}?</p>
          
          {deletePreviewLoading ? (
            <div className="py-4">
              <LoadingSpinner size="sm" />
              <p className="text-sm text-gray-500 mt-2">Loading related data...</p>
            </div>
          ) : deletePreview && deletePreview.totalRecords > 0 ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-left">
              <p className="text-sm font-medium text-red-800 mb-3">⚠️ The following data will be permanently deleted:</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {deletePreview.counts.users > 0 && (
                  <div className="flex items-center gap-2 text-red-700">
                    <Users className="w-4 h-4" />
                    <span>{deletePreview.counts.users} Users</span>
                  </div>
                )}
                {deletePreview.counts.customers > 0 && (
                  <div className="flex items-center gap-2 text-red-700">
                    <span>👥</span>
                    <span>{deletePreview.counts.customers} Customers</span>
                  </div>
                )}
                {deletePreview.counts.locations > 0 && (
                  <div className="flex items-center gap-2 text-red-700">
                    <span>📍</span>
                    <span>{deletePreview.counts.locations} Locations</span>
                  </div>
                )}
                {deletePreview.counts.attendance > 0 && (
                  <div className="flex items-center gap-2 text-red-700">
                    <span>📋</span>
                    <span>{deletePreview.counts.attendance} Attendance Records</span>
                  </div>
                )}
                {deletePreview.counts.leaves > 0 && (
                  <div className="flex items-center gap-2 text-red-700">
                    <span>🏖️</span>
                    <span>{deletePreview.counts.leaves} Leave Requests</span>
                  </div>
                )}
                {deletePreview.counts.holidays > 0 && (
                  <div className="flex items-center gap-2 text-red-700">
                    <span>🎉</span>
                    <span>{deletePreview.counts.holidays} Holidays</span>
                  </div>
                )}
                {deletePreview.counts.payroll > 0 && (
                  <div className="flex items-center gap-2 text-red-700">
                    <span>💰</span>
                    <span>{deletePreview.counts.payroll} Payroll Records</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-red-600 mt-3 font-medium">
                Total: {deletePreview.totalRecords} records will be deleted
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-600 mb-4">This client has no associated data.</p>
          )}
          
          <p className="text-sm text-red-600 mb-6 font-medium">⚠️ This action cannot be undone!</p>
          
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => { setIsDeleteModalOpen(false); setDeletePreview(null); }}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete} loading={deleteLoading}>
              Delete Client & All Data
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reset Password Modal */}
      <Modal isOpen={isResetPasswordModalOpen} onClose={() => setIsResetPasswordModalOpen(false)} title="Reset Admin Password" size="sm">
        <form onSubmit={handleResetPassword} className="space-y-4">
          <p className="text-gray-600">Reset password for <strong>{selected?.name}</strong> admin user.</p>
          <Input
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={6}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" type="button" onClick={() => setIsResetPasswordModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={resetLoading}>Reset Password</Button>
          </div>
        </form>
      </Modal>

      {/* Billing Modal */}
      <Modal isOpen={isBillingModalOpen} onClose={() => { setIsBillingModalOpen(false); setBillingData(null); }} title={`Billing - ${selected?.name}`} size="xl">
        <div className="space-y-6 max-h-[70vh] overflow-y-auto">
          {billingData ? (
            <>
              {/* Current Billing Info */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                  Current Month Billing
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs text-gray-500">Month</p>
                    <p className="font-bold text-lg">{billingData.currentBilling?.month || new Date().toISOString().slice(0, 7)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs text-gray-500">Employees</p>
                    <p className="font-bold text-lg">{billingData.currentBilling?.employeeCount || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs text-gray-500">Per Employee</p>
                    <p className="font-bold text-lg">₹{billingData.currentBilling?.perEmployeeCost || 0}</p>
                  </div>
                  <div className={`rounded-lg p-3 border ${billingData.currentBilling?.isTrial ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                    <p className="text-xs text-gray-500">Amount Due</p>
                    <p className={`font-bold text-lg ${billingData.currentBilling?.isTrial ? 'text-green-600' : 'text-blue-600'}`}>
                      {billingData.currentBilling?.isTrial ? '₹0 (Trial)' : `₹${billingData.currentBilling?.paymentDue || 0}`}
                    </p>
                  </div>
                </div>
                <div className="flex justify-end mt-4">
                  <Button onClick={handleGenerateBilling} loading={billingLoading} size="sm">
                    Generate This Month's Invoice
                  </Button>
                </div>
              </div>

              {/* Billing Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <p className="text-sm text-gray-600">Total Paid</p>
                  <p className="font-bold text-2xl text-green-600">₹{billingData.summary?.totalPaid || 0}</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <p className="text-sm text-gray-600">Pending</p>
                  <p className="font-bold text-2xl text-yellow-600">₹{billingData.summary?.totalPending || 0}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <p className="text-sm text-gray-600">Overdue</p>
                  <p className="font-bold text-2xl text-red-600">₹{billingData.summary?.totalOverdue || 0}</p>
                </div>
              </div>

              {/* Payment History */}
              <div>
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-gray-600" />
                  Payment History
                </h4>
                {billingData.billingHistory?.length > 0 ? (
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Month</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Employees</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Rate</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {billingData.billingHistory.map((payment, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium">{payment.month}</td>
                            <td className="px-4 py-3">{payment.employeeCount}</td>
                            <td className="px-4 py-3">₹{payment.perEmployeeCost}</td>
                            <td className="px-4 py-3 font-bold">₹{payment.totalAmount}</td>
                            <td className="px-4 py-3">
                              <Badge variant={payment.status === 'paid' ? 'success' : payment.status === 'overdue' ? 'danger' : 'warning'}>
                                {payment.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              {payment.status === 'pending' && (
                                <Button size="sm" variant="outline" onClick={() => handleMarkPaid(payment.month)} loading={billingLoading}>
                                  <CheckCircle className="w-4 h-4 mr-1" /> Mark Paid
                                </Button>
                              )}
                              {payment.status === 'paid' && (
                                <span className="text-xs text-gray-500">
                                  Paid on {payment.paidAt ? new Date(payment.paidAt).toLocaleDateString() : 'N/A'}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-xl border border-gray-200">
                    <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No billing history yet</p>
                    <Button onClick={handleGenerateBilling} loading={billingLoading} className="mt-3" size="sm">
                      Generate First Invoice
                    </Button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ClientsPage;
